/**
 * Message Deduplication
 * Extracted from WebSocketManager.ts
 * Prevents duplicate message processing
 */

import { log } from "@/shared/utils/logger";

export class MessageDeduplicator {
  private processedMessages: Set<string> = new Set();
  private messageCleanupTimer: NodeJS.Timeout | null = null;

  // Generate unique ID for a message
  getMessageId(message: Record<string, unknown>): string | null {
    // Generate a unique ID for the message based on its content
    if (message.type === "operation" && message.operation && typeof message.operation === 'object' && !Array.isArray(message.operation)) {
      const op = message.operation;
      const operationId = typeof message.operationId === 'string' ? message.operationId
        : 'id' in op && typeof op.id === 'string' ? op.id : null;
      const projectId = typeof message.projectId === 'string' ? message.projectId
        : typeof message.project_id === 'string' ? message.project_id : '';
      if (operationId) return JSON.stringify(['operation', projectId, operationId]);
      // Without a stable ID only identical frames are duplicates. Timestamp
      // alone can collide for independent updates delivered in one tick.
      return JSON.stringify(['operation', projectId, op, message.timestamp ?? null]);
    }
    if (message.sessionId && message.timestamp) {
      return `${message.sessionId}-${message.timestamp}`;
    }
    return null;
  }

  // Check if message was already processed
  isDuplicate(messageId: string | null): boolean {
    if (!messageId) return false;
    return this.processedMessages.has(messageId);
  }

  // Mark message as processed
  markAsProcessed(messageId: string | null): void {
    if (!messageId) return;
    this.processedMessages.add(messageId);
    this.scheduleMessageCleanup();
  }

  // Schedule cleanup of old processed messages
  private scheduleMessageCleanup(): void {
    // Clean up old processed messages every 5 minutes to prevent memory leak
    if (this.messageCleanupTimer) {
      return; // Already scheduled
    }

    this.messageCleanupTimer = setTimeout(() => {
      // Keep only last 1000 messages
      if (this.processedMessages.size > 1000) {
        const messagesToKeep = Array.from(this.processedMessages).slice(-1000);
        this.processedMessages = new Set(messagesToKeep);
        log.debug(
          "WebSocket",
          `Cleaned up message history, kept last 1000 messages`
        );
      }
      this.messageCleanupTimer = null;
    }, 5 * 60 * 1000); // 5 minutes
  }

  // Clear cleanup timer
  clearCleanupTimer(): void {
    if (this.messageCleanupTimer) {
      clearTimeout(this.messageCleanupTimer);
      this.messageCleanupTimer = null;
    }
  }

  // Clear all processed messages
  clear(): void {
    this.processedMessages.clear();
    this.clearCleanupTimer();
  }
}
